import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { RAISE_BLOCKED_REASON } from '../constants';
import type {
  AllocationRules,
  RechargeRequest,
  RedemptionChannels,
  RewardsSettingsResponse,
  SpecialAward,
} from '../types';

// ─── Types ───────────────────────────────────────────────────────────────────

type LandingPageState = RewardsSettingsResponse;

type RewardsSettingsState = {
  landingPage: LandingPageState;
};

// ─── Initial state ───────────────────────────────────────────────────────────

const initialState: RewardsSettingsState = {
  landingPage: {
    premiumFeatureEnabled: false,
    rewardsActivated: false,
    budgetSummary: null,
    redemptionSummary: null,
    giftCardAccount: null,
    specialAwards: [],
    allocationRules: null,
    redemptionChannels: null,
    categories: [],
  },
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const rewardsSettingsSlice = createSlice({
  name: 'rewardsSettings',
  initialState,
  reducers: {
    initializeLandingPage: (state, action: PayloadAction<RewardsSettingsResponse>) => {
      state.landingPage = action.payload;
    },
    updateRedemptionChannels: (state, action: PayloadAction<Partial<RedemptionChannels>>) => {
      if (!state.landingPage.redemptionChannels) return;
      state.landingPage.redemptionChannels = {
        ...state.landingPage.redemptionChannels,
        ...action.payload,
      };
    },
    updateAllocationRules: (state, action: PayloadAction<Partial<AllocationRules>>) => {
      if (!state.landingPage.allocationRules) return;
      state.landingPage.allocationRules = {
        ...state.landingPage.allocationRules,
        ...action.payload,
      };
    },
    updateCategories: (state, action: PayloadAction<string[]>) => {
      state.landingPage.categories = action.payload;
    },
    updateSpecialAwards: (state, action: PayloadAction<SpecialAward[]>) => {
      state.landingPage.specialAwards = action.payload;
    },
    // A top up was raised (or one already existed): show it as in progress without refetching the settings,
    // which would overwrite any unsaved edits on the page. Guarded on company id: the account in state may
    // belong to a company the user has since switched away from (a late response, or a stale 409), and this
    // request must never be attached to the wrong company's account.
    markGiftCardTopUpInProgress: (state, action: PayloadAction<RechargeRequest>) => {
      if (!state.landingPage.giftCardAccount) return;
      if (state.landingPage.giftCardAccount.company.netchexCompanyId !== action.payload.netchexCompanyId) return;
      state.landingPage.giftCardAccount = {
        ...state.landingPage.giftCardAccount,
        openRequest: action.payload,
        canRaiseRequest: false,
        raiseBlockedReason: RAISE_BLOCKED_REASON.REQUEST_IN_PROGRESS,
      };
    },
  },
});

// ─── Exports ─────────────────────────────────────────────────────────────────

export const {
  initializeLandingPage,
  updateRedemptionChannels,
  updateAllocationRules,
  updateCategories,
  updateSpecialAwards,
  markGiftCardTopUpInProgress,
} = rewardsSettingsSlice.actions;

export default rewardsSettingsSlice.reducer;

// ─── Selectors ───────────────────────────────────────────────────────────────

type RootState = { rewardsSettings: RewardsSettingsState };

export const useSelectLandingPage = () => useSelector((state: RootState) => state.rewardsSettings.landingPage);

export const useSelectRedemptionChannels = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.redemptionChannels);

export const useSelectAllocationRules = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.allocationRules);

export const useSelectCategories = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.categories);

export const useSelectSpecialAwards = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.specialAwards);

export const useSelectPremiumFeatureEnabled = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.premiumFeatureEnabled);

export const useSelectRewardsActivated = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.rewardsActivated);

export const useSelectBudgetSummary = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.budgetSummary);

export const useSelectRedemptionSummary = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.redemptionSummary);

export const useSelectGiftCardAccount = () =>
  useSelector((state: RootState) => state.rewardsSettings.landingPage.giftCardAccount);
